import { Icon } from '@hologram-dimension/icon';
import { Popover } from '@hologram-dimension/popover';
import { clsx } from 'clsx/lite';
import { Fragment, type FC } from 'react';
import { ProfilesDaoSimProfileDetails } from 'types/__generated__/SimInventoryApi';
import useAnalytics from 'state/contexts/Analytics/useAnalytics';
import styles from './ApnInfoIcon.module.css';

interface ApnInfoIconProps {
  profiles: ProfilesDaoSimProfileDetails[];
  className?: string;
  placement?: GetReactParameterBag<typeof Popover>['placement'];
  autoPlace?: boolean;
}

const ApnInfoIcon: FC<ApnInfoIconProps> = ({
  profiles,
  className,
  placement = 'top',
  autoPlace = false,
}) => {
  const { sendAnalyticsEvent } = useAnalytics();

  if (!profiles) return null;

  const uniqueProfiles: ApnInfoIconProps['profiles'] = [];
  profiles.forEach((profile) => {
    if (
      profile.requiredApn &&
      !uniqueProfiles.find((uniqueApn) => uniqueApn.requiredApn === profile.requiredApn)
    ) {
      // Is this the APN of the enabled profile? (in case there are two profiles matching this APN name, but one is enabled)
      const matchingSiblingProfile = profiles.find(
        (siblingProfile) =>
          siblingProfile.installationState === 'ENABLED' &&
          siblingProfile.requiredApn === profile.requiredApn
      );
      if (matchingSiblingProfile) {
        uniqueProfiles.push(matchingSiblingProfile);
      } else {
        uniqueProfiles.push(profile);
      }
    }
  });

  return (
    <div className={clsx(styles.wrapper, className)}>
      <div className={styles.apnName}>
        {uniqueProfiles.map(({ simProfileId, installationState, requiredApn }, index) => (
          <Fragment key={simProfileId}>
            {installationState === 'ENABLED' || uniqueProfiles.length === 1 ? (
              requiredApn
            ) : (
              <span className={styles.diminished}>{requiredApn}</span>
            )}
            {index + 1 < uniqueProfiles.length && <span className={styles.diminished}>/</span>}
          </Fragment>
        ))}
      </div>
      <Popover
        content={
          uniqueProfiles.length > 1
            ? 'Your SIM card has profiles that require different Access Point Names (APNs). The device this SIM is in will need to switch APNs if the profile changes.'
            : "Your device should be set to this Access Point Name (APN) based on your SIM's active profile. The APN may change if the SIM profile changes."
        }
        id="apn-popover"
        className={styles.popover}
        autoPlace={autoPlace}
        placement={placement}
        onOpen={() => {
          if (sendAnalyticsEvent)
            sendAnalyticsEvent(
              `Info - ${uniqueProfiles.length > 1 ? 'Required multiple APN' : 'Required single APN'}`
            );
        }}
      >
        <Icon
          name="CircleInfo"
          fill="DdsColorInteractivePrimaryDefault"
          className={styles.apnInfoIcon}
        />
      </Popover>
    </div>
  );
};

export default ApnInfoIcon;
